import type { Priority, Status, StatusType, ViewSettings, ViewType } from './types.ts';

/** Color palette offered for spaces, statuses, options and tags. */
export const PALETTE = [
  '#87909e', // gray
  '#1090e0', // blue
  '#4466ff', // royal
  '#5f55ee', // indigo
  '#3e63dd', // ocean
  '#b660e0', // purple
  '#ab4aba', // plum
  '#ee5e99', // pink
  '#f39cbf', // blush
  '#e5484d', // red
  '#f76808', // orange
  '#e16b16', // amber
  '#f8ae00', // yellow
  '#ffc53d', // sunflower
  '#0f9d9f', // teal
  '#3db88b', // mint
  '#008844', // green
  '#0e4b0e', // forest
  '#aa8d80', // taupe
  '#656f7d', // slate
];

export const PRIORITIES: { value: Priority; label: string; color: string }[] = [
  { value: 1, label: 'Urgent', color: '#e5484d' },
  { value: 2, label: 'High', color: '#f8ae00' },
  { value: 3, label: 'Normal', color: '#4466ff' },
  { value: 4, label: 'Low', color: '#87909e' },
];

export function priorityMeta(p: Priority | null | undefined) {
  return PRIORITIES.find((x) => x.value === p) ?? null;
}

export const STATUS_TYPE_LABEL: Record<StatusType, string> = {
  open: 'Not started',
  unstarted: 'Not started',
  custom: 'Active',
  done: 'Done',
  closed: 'Closed',
};

/** Status groups in the order ClickUp-style editors show them. */
export const STATUS_GROUPS: { key: 'notStarted' | 'active' | 'done' | 'closed'; label: string; types: StatusType[] }[] = [
  { key: 'notStarted', label: 'Not started', types: ['open', 'unstarted'] },
  { key: 'active', label: 'Active', types: ['custom'] },
  { key: 'done', label: 'Done', types: ['done'] },
  { key: 'closed', label: 'Closed', types: ['closed'] },
];

export function isCompleteType(t: StatusType) {
  return t === 'done' || t === 'closed';
}

export function defaultStatuses(newId: () => string): Status[] {
  return [
    { id: newId(), name: 'to do', color: '#87909e', type: 'open', orderIndex: 0 },
    { id: newId(), name: 'in progress', color: '#5f55ee', type: 'custom', orderIndex: 1 },
    { id: newId(), name: 'complete', color: '#008844', type: 'closed', orderIndex: 2 },
  ];
}

export interface BuiltinField {
  id: string;
  label: string;
  icon: string; // icon name (ICONS key in web/src/components/ui/Icon.tsx)
  width: number;
  sortable: boolean;
  groupable: boolean;
}

/** Built-in task fields usable as columns, sort keys and filters. */
export const BUILTIN_FIELDS: BuiltinField[] = [
  { id: 'name', label: 'Name', icon: 'Type', width: 360, sortable: true, groupable: false },
  { id: 'assignee', label: 'Assignee', icon: 'UserRound', width: 160, sortable: true, groupable: true },
  { id: 'dueDate', label: 'Due date', icon: 'Calendar', width: 160, sortable: true, groupable: true },
  { id: 'priority', label: 'Priority', icon: 'Flag', width: 160, sortable: true, groupable: true },
  { id: 'status', label: 'Status', icon: 'CircleDot', width: 160, sortable: true, groupable: true },
  { id: 'startDate', label: 'Start date', icon: 'CalendarClock', width: 160, sortable: true, groupable: false },
  { id: 'dateCreated', label: 'Date created', icon: 'CalendarPlus', width: 160, sortable: true, groupable: false },
  { id: 'dateUpdated', label: 'Date updated', icon: 'CalendarSync', width: 160, sortable: true, groupable: false },
  { id: 'dateClosed', label: 'Date closed', icon: 'CalendarCheck', width: 160, sortable: true, groupable: false },
  { id: 'timeEstimate', label: 'Time estimate', icon: 'Hourglass', width: 160, sortable: true, groupable: false },
  { id: 'timeTracked', label: 'Time tracked', icon: 'Timer', width: 160, sortable: true, groupable: false },
  { id: 'tags', label: 'Tags', icon: 'Tag', width: 180, sortable: false, groupable: true },
  { id: 'taskType', label: 'Task type', icon: 'Shapes', width: 160, sortable: true, groupable: true },
  { id: 'createdBy', label: 'Created by', icon: 'UserPlus', width: 160, sortable: true, groupable: false },
  { id: 'latestComment', label: 'Latest comment', icon: 'MessageSquare', width: 180, sortable: false, groupable: false },
  { id: 'list', label: 'List', icon: 'ListTodo', width: 180, sortable: true, groupable: true },
  { id: 'taskId', label: 'Task ID', icon: 'Hash', width: 140, sortable: true, groupable: false },
  { id: 'progress', label: 'Progress', icon: 'ChartPie', width: 160, sortable: false, groupable: false },
];

export function builtinField(id: string) {
  return BUILTIN_FIELDS.find((f) => f.id === id);
}

export function defaultViewSettings(type: ViewType): ViewSettings {
  return {
    groupBy: type === 'calendar' || type === 'gantt' ? 'none' : 'status',
    groupDir: 'asc',
    sort: [],
    filters: { op: 'and', rules: [] },
    subtasks: 'collapsed',
    showClosed: false,
    meMode: false,
    columns: [
      { id: 'assignee' },
      { id: 'dueDate' },
      { id: 'priority' },
      { id: 'status', hidden: true },
      { id: 'dateCreated', hidden: true },
      { id: 'timeEstimate', hidden: true },
      { id: 'tags', hidden: true },
    ],
    collapsedGroups: [],
    calendarMode: 'month',
    ganttZoom: 'week',
    showEmptyStatuses: type === 'board',
  };
}

export const VIEW_TYPE_META: Record<ViewType, { label: string; icon: string; color: string; description: string }> = {
  list: { label: 'List', icon: 'List', color: '#87909e', description: 'Track tasks, bugs, people & more' },
  board: { label: 'Board', icon: 'SquareKanban', color: '#4466ff', description: 'Move tasks between columns' },
  calendar: { label: 'Calendar', icon: 'CalendarDays', color: '#e16b16', description: 'Plan, schedule, & delegate' },
  table: { label: 'Table', icon: 'Sheet', color: '#3db88b', description: 'Structured table format' },
  gantt: { label: 'Gantt', icon: 'ChartGantt', color: '#e5484d', description: 'Plan dependencies & time' },
  doc: { label: 'Doc', icon: 'FileText', color: '#1090e0', description: 'Collaborate & document anything' },
  chat: { label: 'Channel', icon: 'Hash', color: '#5f55ee', description: 'Discuss anything with your team' },
  overview: { label: 'Overview', icon: 'ChartPie', color: '#7b68ee', description: 'A space at a glance: lists, docs, files & workload' },
};
